import axios from 'axios';
import { API_URL } from '../config/api';
import { requestNotificationPermission } from './notifications';

/**
 * Get VAPID public key from backend for push subscription.
 * @returns {Promise<{ key: string }|{ key: null, reason: 'server'|'network' }>}
 */
export async function getVapidPublicKey() {
  try {
    const res = await axios.get(`${API_URL}/users/vapid-public-key`);
    const raw = res.data?.publicKey;
    const key = typeof raw === 'string' ? raw.trim().replace(/\s+/g, '') : null;
    return key ? { key } : { key: null, reason: 'server' };
  } catch (err) {
    if (err.response?.status === 503) {
      console.warn('Push: server not configured (VAPID keys missing in production).');
      return { key: null, reason: 'server' };
    }
    console.error('Failed to get VAPID public key:', err);
    return { key: null, reason: 'network' };
  }
}

/**
 * Subscribe to push in the service worker and return the subscription object
 * (endpoint + keys) to send to the backend.
 * @param {string} vapidPublicKey - Base64 VAPID public key
 * @returns {Promise<PushSubscription|null>}
 */
export async function subscribeToPush(vapidPublicKey) {
  const key = typeof vapidPublicKey === 'string' ? vapidPublicKey.trim().replace(/\s+/g, '') : '';
  if (!key) return null;
  const granted = await requestNotificationPermission();
  if (!granted) return null;

  // Use the registration that controls this scope (same one that will receive push events)
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key)
  });
  return sub;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Serialize PushSubscription to the shape the backend expects.
 * @param {PushSubscription} subscription
 * @returns {{ endpoint: string, keys: { p256dh: string, auth: string }, expirationTime?: number }}
 */
export function subscriptionToPayload(subscription) {
  const json = subscription.toJSON();
  return {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh || '',
      auth: json.keys?.auth || ''
    },
    ...(json.expirationTime != null && { expirationTime: json.expirationTime })
  };
}

/**
 * Register push subscription with the backend (requires auth).
 * @param {object} payload - Result of subscriptionToPayload(subscription)
 * @returns {Promise<boolean>}
 */
export async function sendSubscriptionToBackend(payload) {
  try {
    await axios.post(`${API_URL}/users/push-subscription`, payload);
    return true;
  } catch (err) {
    console.error('Failed to send push subscription:', err);
    return false;
  }
}

/**
 * Full flow: get VAPID key, subscribe in SW, send subscription to backend.
 * Call when user enables push notifications and is logged in.
 * @returns {Promise<{ ok: boolean, reason?: 'server'|'permission'|'unsupported'|'network' }>}
 */
export async function registerPushSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  const { key, reason } = await getVapidPublicKey();
  if (!key) return { ok: false, reason: reason || 'server' };
  const sub = await subscribeToPush(key);
  if (!sub) return { ok: false, reason: 'permission' };
  const payload = subscriptionToPayload(sub);
  const sent = await sendSubscriptionToBackend(payload);
  return sent ? { ok: true } : { ok: false, reason: 'network' };
}
